import { Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  Firestore,
  getDocs,
  onSnapshot,
  Unsubscribe,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Map } from 'immutable';
import { BehaviorSubject, Observable } from 'rxjs';

export class Note {
  constructor(
    public text: string,
    public timestamp: Date,
    public id?: string
  ) {}
}

const NoteConverter = {
  toFirestore: (note: Note) => {
    return {
      text: note.text,
      timestamp: note.timestamp,
    };
  },
  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options);
    return new Note(
      data.text,
      new Date(data.timestamp.seconds * 1000),
      snapshot.id
    );
  },
};

@Injectable({
  providedIn: 'root',
})
export class NotesService {
  currentDocs: BehaviorSubject<Map<string, Note>>;
  unsub: Unsubscribe[];

  constructor(private firestore: Firestore, private auth: Auth) {
    this.refreshService();
  }

  private resetSubs() {
    if (this.unsub) {
      for (let unsub of this.unsub) {
        unsub();
      }
    }
    this.unsub = [];
  }

  public refreshService() {
    this.resetSubs();
    this.currentDocs = new BehaviorSubject<Map<string, Note>>(
      Map<string, Note>()
    );

    // refresh data on auth state change
    this.unsub.push(
      this.auth.onAuthStateChanged((user) => {
        if (user) {
          this.subscribeToNoteChanges();
          return this.updateDocs();
        }
      })
    );

  }

  private userNotesCollection() {
    const notePaths = 'userData/' + this.auth.currentUser.uid + '/notes';
    return collection(this.firestore, notePaths);
  }

  private subscribeToNoteChanges() {
    this.unsub.push(
      onSnapshot(this.userNotesCollection(), (data) => {
        this.currentDocs.next(
          this.currentDocs.getValue().withMutations((mutableMap) => {
            data.docChanges().forEach((value) => {
              const note = NoteConverter.fromFirestore(value.doc, {});
              mutableMap.set(note.id, note);
            });
          })
        );
      })
    );
  }

  /**
   * Should only be called on constructor
   * @private
   */
  private async updateDocs() {
    const notesSnapshot = await getDocs(this.userNotesCollection());

    const listOfData = notesSnapshot.docs.map((doc) => {
      return NoteConverter.fromFirestore(doc, {});
    });

    const dataMap = Map<string, Note>();
    this.currentDocs.next(
      dataMap.withMutations((dataMap) => {
        for (let value of listOfData) {
          dataMap = dataMap.set(value.id, value);
        }
      })
    );
  }

  public getNotes() {
    return this.currentDocs.getValue();
  }

  public getNotesUpdatedObservable(): Observable<Map<string, Note>> {
    return this.currentDocs.asObservable();
  }

  public async createNote(note: string) {
    const noteObj = new Note(note, new Date());
    const result = await addDoc(
      this.userNotesCollection(),
      NoteConverter.toFirestore(noteObj)
    );
    noteObj.id = result.id;
    return noteObj;
  }
}
